import type Luminary from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

/**
 * A non-looping track that stops before the video ends leaves a silent tail. A short quiet outro is
 * a normal, deliberate ending, so this case is judged leniently — unlike the loop case, which fires
 * on any shortfall.
 */
const PREMATURE_END_TOLERANCE_MS = 5000;

const seconds = (ms: number): string => (ms / 1000).toFixed(1);

/**
 * The video's music track is shorter than the video, so it cannot cover the whole runtime on a
 * single pass. What that produces depends on the track's loop setting, which background music has on
 * by default:
 *
 *  - Looping: the track restarts to fill the video. Any repeat is an error — the build should pick a
 *    track that spans the whole video — since a loop seam is jarring on louder music.
 *  - Not looping: the track stops early and the tail plays silent. A short quiet outro is a normal
 *    ending, so this is only a warning, and only when the silent tail is long.
 */
export function checkMusicTooShort(luminary: Luminary): LintFinding[] {
    const musicAsset = luminary.musicAsset;
    if (!musicAsset) {
        return [];
    }

    const musicMs = musicAsset.duration;
    const videoMs = luminary.duration;
    if (!Number.isFinite(musicMs) || !Number.isFinite(videoMs) || musicMs <= 0 || videoMs <= 0) {
        return [];
    }

    const shortfallMs = videoMs - musicMs;
    if (shortfallMs <= 0) {
        return [];
    }

    if (musicAsset.loop) {
        const timesThrough = (videoMs / musicMs).toFixed(1);
        return [
            {
                check: 'music_too_short',
                severity: 'error',
                message:
                    `Music track is ${seconds(musicMs)}s but the video runs ${seconds(videoMs)}s, ` +
                    `so the track loops to fill it (plays through about ${timesThrough} times). ` +
                    `A short, obvious loop sounds repetitive — pick a track that spans the whole ` +
                    `video.`,
            },
        ];
    }

    if (shortfallMs <= PREMATURE_END_TOLERANCE_MS) {
        return [];
    }
    return [
        {
            check: 'music_too_short',
            severity: 'warning',
            message:
                `Music track is ${seconds(musicMs)}s but the video runs ${seconds(videoMs)}s, ` +
                `so the last ${seconds(shortfallMs)}s plays with no music. A short quiet outro is ` +
                `usually fine — pick a longer track only if the silent tail is too long.`,
        },
    ];
}
